import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Handshake, Truck, Mail, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';

type Versandart = { id: number; code: string; label: string };

type Props = {
  aktuell: Versandart | null;
  optionen: Versandart[];
  onSelect: (id: number) => void;
  /** Aus Shopify: nur anzeigen (E47). */
  gesperrt?: boolean;
};

// Dieselbe Zuordnung wie in der Dashboard-Spalte, damit dasselbe Ding
// nicht an zwei Orten verschieden aussieht.
function icon(code: string | null) {
  if (code === null) return Truck;
  if (code.startsWith('abhol')) return Handshake;
  if (code.startsWith('post')) return Mail;
  return Truck;
}

/**
 * Zeigt die Versandart im Detail-Kopf und laesst sie dort aendern.
 *
 * Bis hierher war die Versandart in der Detailansicht nirgends sichtbar —
 * nur als Symbol im Dashboard. Wer eine Bestellung offen hatte, konnte
 * nicht sehen, ob sie abgeholt oder verschickt wird, und musste zum
 * Aendern den Umweg ueber den Sendungs-Dialog nehmen.
 */
export function VersandartChip({ aktuell, optionen, onSelect, gesperrt = false }: Props) {
  const Icon = icon(aktuell?.code ?? null);
  const gesetzt = aktuell !== null;

  // Aus Shopify (E47): Die Versandart ist die Versandwahl im Shop. Sie
  // steht hier als Auskunft, ohne Pfeil und ohne Menü.
  if (gesperrt) {
    return (
      <span
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-line bg-cream px-3 text-sm text-ink"
        title={gesetzt ? `Lieferung: ${aktuell.label} — aus Shopify` : 'Lieferung noch nicht festgelegt'}
      >
        <Icon className="h-4 w-4" />
        {gesetzt ? aktuell.label : 'Lieferung offen'}
      </span>
    );
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={clsx(
            'inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-3 text-sm',
            gesetzt
              ? 'border-line bg-cream text-ink hover:border-akzent hover:text-akzent'
              : 'border-dashed border-line bg-cream text-ink-faint hover:border-akzent hover:text-akzent'
          )}
          title={gesetzt ? `Lieferung: ${aktuell.label}` : 'Lieferung noch nicht festgelegt'}
        >
          <Icon className="h-4 w-4" />
          {gesetzt ? aktuell.label : 'Lieferung wählen'}
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="z-50 min-w-[12rem] rounded-md border border-line bg-cream p-1 shadow-lg"
        >
          {optionen.map((v) => {
            const VIcon = icon(v.code);
            return (
              <DropdownMenu.Item
                key={v.id}
                onSelect={() => onSelect(v.id)}
                className={clsx(
                  'flex min-h-[44px] cursor-pointer items-center gap-2 rounded px-3 text-sm outline-none',
                  v.id === aktuell?.id
                    ? 'bg-akzent-sanft text-akzent-stark'
                    : 'text-ink-soft data-[highlighted]:bg-cream-dark data-[highlighted]:text-ink'
                )}
              >
                <VIcon className="h-4 w-4" />
                {v.label}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
